import React, { useEffect, useState } from 'react';
import { ChevronRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

export const DemoStepSegmentation = ({ onNext }) => {
    const { t } = useTranslation();
    const { brand, segmentation } = useBrandConfig();

    const [isVisible, setIsVisible] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleValidate = () => {
        if (selectedOption) {
            onNext({ favoriteProduct: selectedOption });
        }
    };

    const customStyles = `
        .stagger-1 { transition-delay: 100ms; }
        .stagger-2 { transition-delay: 200ms; }
        .stagger-3 { transition-delay: 300ms; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .bg-dots { background-size: 24px 24px; background-image: radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px); }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col" style={{ backgroundColor: brand.colors.pageBg }}>
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* HEADER */}
            <div className="w-full pt-16 pb-3 flex justify-center items-center z-20 relative bg-white/20 backdrop-blur-md shrink-0 border-b border-white/30">
                <BrandLogo className="h-7 object-contain" />
            </div>

            {/* CONTENU */}
            <div className="flex-1 overflow-y-auto pb-48 hide-scrollbar relative bg-dots px-6">
                <div className="flex flex-col items-center min-h-full max-w-sm mx-auto py-8 gap-8">

                    <div className={`text-center flex flex-col items-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-1`}>
                        <div className="bg-white/40 backdrop-blur-md px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest text-black/60 shadow-sm mb-4">
                            {t('holy.segmentation.badge')}
                        </div>
                        <h1 className="font-heading text-[3.5rem] leading-[0.85] text-black uppercase italic tracking-tighter drop-shadow-sm">
                            {t('holy.segmentation.title_line1')} <br />
                            <span className="text-white drop-shadow-md">{t('holy.segmentation.title_line2')}</span>
                        </h1>
                    </div>

                    {/* GRILLE 2x2 */}
                    <div className={`w-full grid grid-cols-2 gap-4 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-2`}>
                        {segmentation.products.map((item, index) => {
                            const isSelected = selectedOption === item.id;
                            const label = item.label ?? t(`holy.segmentation.product_${item.id}_label`);
                            const desc  = item.desc  ?? t(`holy.segmentation.product_${item.id}_desc`);

                            return (
                                <div
                                    key={item.id}
                                    id={index === 0 ? 'demo-btn-product' : undefined}
                                    onClick={() => setSelectedOption(item.id)}
                                    className="relative group bg-white/30 backdrop-blur-3xl rounded-[2rem] p-4 flex flex-col items-center justify-between aspect-square cursor-pointer transition-all duration-300 active:scale-95 border-2"
                                    style={isSelected ? {
                                        borderColor: brand.colors.accent,
                                        boxShadow: `0 10px 30px ${brand.colors.accent}4D`,
                                        backgroundColor: 'rgba(255,255,255,0.5)'
                                    } : { borderColor: 'rgba(255,255,255,0.4)' }}
                                >
                                    {isSelected && (
                                        <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center shadow-sm animate-fade-in z-20" style={{ backgroundColor: brand.colors.accent }}>
                                            <Check size={14} strokeWidth={4} style={{ color: brand.colors.accentText }} />
                                        </div>
                                    )}

                                    <div className="flex-1 flex items-center justify-center w-full relative mb-2">
                                        <img
                                            src={item.imgUrl}
                                            alt={label}
                                            className={`max-h-24 object-contain drop-shadow-xl transition-transform duration-300 ${isSelected ? 'scale-110' : 'group-hover:scale-105'}`}
                                            onError={(e) => { e.target.src = brand.logoUrl; }}
                                        />
                                    </div>

                                    <div className="text-center w-full">
                                        <h3 className="font-heading italic text-xl uppercase text-black leading-none tracking-wide">{label}</h3>
                                        <span className="text-[9px] font-black text-black/50 uppercase tracking-widest mt-1 block">{desc}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* CTA STICKY */}
            <div
                className={`absolute bottom-0 left-0 w-full px-6 pb-8 pt-16 to-transparent z-30 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-3`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg}, ${brand.colors.pageBg} 60%, transparent)` }}
            >
                <div className="max-w-sm mx-auto">
                    <button
                        id="demo-btn-segment"
                        onClick={handleValidate}
                        disabled={!selectedOption}
                        className="w-full flex items-center justify-center gap-3 py-5 rounded-full font-heading text-2xl uppercase italic tracking-wider transition-all duration-300"
                        style={selectedOption ? {
                            backgroundColor: brand.colors.accent,
                            color: brand.colors.accentText,
                            boxShadow: `0 12px 40px ${brand.colors.accent}66`
                        } : {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            color: 'rgba(0,0,0,0.3)',
                            cursor: 'not-allowed'
                        }}
                    >
                        {t('holy.segmentation.validate_btn')}
                        <ChevronRight size={28} strokeWidth={3} />
                    </button>
                </div>
            </div>
        </div>
    );
};
